import { apiClient, type Paginated } from '@/core/api';
import type { Member, MemberInput, Role } from './types';

const ROLES_BASE = '/roles/';

const BASE = '/members/';

export interface MemberListParams {
  page?: number;
  /** Rows per page (the API's PageSizePagination honors this). */
  page_size?: number;
  /** SearchFilter across user first/last name + email. */
  search?: string;
  type?: Member['type'];
  is_active?: boolean;
  /** Role title (case-insensitive exact match). */
  role?: string;
  /** Restrict to any of these role titles (exact). Maps to `?role_in=a,b`. */
  roles?: readonly string[];
  /** Filter to the membership of a specific user id (label resolution). */
  user?: number;
  /** Scope to members of a specific project (id) — e.g. worklog Logger picker. */
  project?: number;
  /** Include members whose employee record is terminated (roster only; pickers
   *  exclude them by default). */
  include_inactive?: boolean;
  /** Roster status bucket — mirrors the Status badge (pending wins). */
  status?: 'active' | 'pending' | 'inactive';
  ordering?: string;
}

export async function listMembers(params: MemberListParams = {}): Promise<Paginated<Member>> {
  const { roles, ...rest } = params;
  const { data } = await apiClient.get<Paginated<Member>>(BASE, {
    // `role_in` is a comma-joined multi-value filter on the backend.
    params: { ...rest, role_in: roles && roles.length > 0 ? roles.join(',') : undefined },
  });
  return data;
}

export async function getMember(id: number): Promise<Member> {
  const { data } = await apiClient.get<Member>(`${BASE}${id}/`);
  return data;
}

/** Invite/add a person to the active org (org-admin only). */
export async function inviteMember(input: MemberInput): Promise<Member> {
  const { data } = await apiClient.post<Member>(BASE, input);
  return data;
}

/** Global roles available to assign when inviting a member. */
export async function listRoles(): Promise<Role[]> {
  const { data } = await apiClient.get<Paginated<Role> | Role[]>(ROLES_BASE);
  return Array.isArray(data) ? data : data.results;
}

/** Re-send the set-password invite email to a member who hasn't signed in yet. */
export async function resendInvite(id: number): Promise<Member> {
  const { data } = await apiClient.post<Member>(`${BASE}${id}/resend-invite/`);
  return data;
}

/** Toggle a guest member's white-label (subcontracted) flag (org-admins only). */
export async function setMemberWhiteLabel(id: number, whiteLabel: boolean): Promise<Member> {
  const { data } = await apiClient.patch<Member>(`${BASE}${id}/white-label/`, {
    white_label: whiteLabel,
  });
  return data;
}

/** Deactivate / reactivate a member (org-admins only) — reversible. */
export async function setMemberActive(id: number, isActive: boolean): Promise<Member> {
  const { data } = await apiClient.patch<Member>(`${BASE}${id}/active/`, {
    is_active: isActive,
  });
  return data;
}

/** Replace a member's free-text tags (org-admins only). */
export async function setMemberTags(id: number, tags: string[]): Promise<Member> {
  const { data } = await apiClient.patch<Member>(`${BASE}${id}/tags/`, { tags });
  return data;
}
